/* eslint-disable react-refresh/only-export-components */
import { createRoute, redirect, useRouter } from "@tanstack/react-router"
import { getGetAuthUserQueryKey, getGetOrgObsListQueryKey, getGetOrgObsListQueryOptions } from "openapi/api/endpoints"

import { OrganizationSelectorDialog } from "@/components/organization-selector"
import { queryClient } from "@/lib/query-client"
import AuthenticatedRoute from "./AuthenticatedRoute"

/** The user's memberships, cached for the session's navigations. */
export const organizationsQueryOptions = getGetOrgObsListQueryOptions({ query: { staleTime: 5 * 60_000 } })

/**
 * Stores the org every request is sent for. Query keys aren't org-scoped, so
 * everything cached under the previous org is dropped (except who the user is
 * and which orgs they have).
 */
export function selectOrganization(organizationId: string) {
  const keep = [getGetAuthUserQueryKey()[0], getGetOrgObsListQueryKey()[0]] as unknown[]
  queryClient.removeQueries({ predicate: (query) => !keep.includes(query.queryKey[0]) })
  localStorage.setItem("currentOrganization", organizationId)
}

/** Only same-app paths, so the param can't send the user to another site. */
const safeRedirect = (target: string | undefined) =>
  target?.startsWith("/") && !target.startsWith("//") ? target : "/dashboard"

/**
 * The org picker. The workspace route sends users here when the stored org
 * isn't one of theirs; the HTTP client sends them here (with `rejected`) when
 * the backend refuses the stored org mid-session. The rejected org is hidden so
 * a backend that lists an org but refuses it can't bounce the user in a loop.
 */
const SelectOrganizationRoute = createRoute({
  getParentRoute: () => AuthenticatedRoute,
  path: "/select-organization",
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    rejected: typeof search.rejected === "string" ? search.rejected : undefined,
  }),
  beforeLoad: async ({ search }) => {
    const organizations = (await queryClient.ensureQueryData(organizationsQueryOptions)).flatMap(
      (org) => (org.id && org.id !== search.rejected ? [{ id: org.id, name: org.name || "Unnamed organization" }] : []),
    )
    if (organizations.length === 0) throw redirect({ to: "/no-access", replace: true })
    return { organizations }
  },
  component: SelectOrganization,
})
export default SelectOrganizationRoute

function SelectOrganization() {
  const router = useRouter()
  const { organizations } = SelectOrganizationRoute.useRouteContext()
  const { redirect: target } = SelectOrganizationRoute.useSearch()

  return (
    <>
      <OrganizationSelectorDialog
        organizations={organizations}
        onSelect={(organizationId) => {
          selectOrganization(organizationId)
          router.history.replace(safeRedirect(target))
        }}
      />
      <div className="h-screen bg-background" />
    </>
  )
}
